import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import PlatformCard from '@/components/base/PlatformCard';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';
import { platforms } from '@/mocks/platforms';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    const homepageCategory = categories[0];
    return (
      <>
        <SEOHead
          title={`${homepageCategory.name} - 欧美无码正版平台推荐指南`}
          description={homepageCategory.description}
          keywords="欧美无码,欧美无码高清,欧美无码正版平台"
          canonicalUrl={`https://massdreamer.cn/category/${homepageCategory.slug}`}
        />
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-24 px-4 md:px-6 pb-16">
            <div className="max-w-7xl mx-auto text-center py-20">
              <h1 className="text-2xl font-heading font-bold text-foreground-800">分类未找到</h1>
              <Link to="/" className="mt-4 inline-block text-primary-500 hover:underline cursor-pointer">返回首页</Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const categoryPlatforms = platforms.filter((p) => p.category === category.slug);
  const categoryArticles = articles.filter((a) => a.category === category.slug);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 欧美无码平台推荐`,
    description: category.description,
    url: `https://massdreamer.cn/category/${category.slug}`,
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - 欧美无码正版平台推荐指南`}
        description={category.description}
        keywords={`欧美无码,欧美无码${category.name},欧美无码正版平台,欧美无码资源推荐`}
        canonicalUrl={`https://massdreamer.cn/category/${category.slug}`}
        schema={categorySchema}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 px-4 md:px-6 pb-16">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: category.name },
              ]}
            />

            <div className="mt-8 mb-12">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-xl bg-primary-500/10 flex items-center justify-center">
                  <i className={`${category.icon} text-primary-500 text-2xl`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-800">
                    {category.name}平台推荐
                  </h1>
                  <p className="text-sm text-foreground-500 mt-1">
                    {category.count} 个推荐平台
                  </p>
                </div>
              </div>
              <p className="text-sm text-foreground-500 leading-relaxed max-w-2xl">
                {category.description}
              </p>
            </div>

            {categoryPlatforms.length > 0 && (
              <section className="mb-14">
                <h2 className="text-xl font-heading font-semibold text-foreground-800 mb-6">
                  推荐平台
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {categoryPlatforms.map((platform) => (
                    <PlatformCard key={platform.id} {...platform} />
                  ))}
                </div>
              </section>
            )}

            {categoryArticles.length > 0 && (
              <section>
                <h2 className="text-xl font-heading font-semibold text-foreground-800 mb-6">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {categoryArticles.map((article) => (
                    <ArticleCard key={article.slug} {...article} />
                  ))}
                </div>
              </section>
            )}

            {categoryPlatforms.length === 0 && categoryArticles.length === 0 && (
              <div className="text-center py-16">
                <p className="text-foreground-500">暂无相关内容</p>
              </div>
            )}

            <div className="mt-14 pt-10 border-t border-background-200/50">
              <h3 className="text-lg font-heading font-semibold text-foreground-800 mb-5">
                探索其他分类
              </h3>
              <div className="flex flex-wrap gap-3">
                {categories
                  .filter((c) => c.slug !== slug)
                  .map((c) => (
                    <Link
                      key={c.id}
                      to={`/category/${c.slug}`}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/50 hover:border-primary-500/20 transition-all duration-200 cursor-pointer text-sm"
                    >
                      <i className={`${c.icon} text-primary-500`}></i>
                      <span className="text-foreground-600 whitespace-nowrap">{c.name}</span>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}